import React, { useState } from 'react';

import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { formatToCurrencyCeil } from '../../helpers/formatToCurrencyCeil';
import type {
  IDeleteFacilityAndLocationSettingsData,
  IFacilityLevelActiveWithMultiplicationFactorValue,
  ISaveFacilityAndLocationSettingsData,
} from '../../model/multiplicationFactors';
import { useDeleteSettingsMutation } from '../../services/useDeleteSettingsMutation';
import { useSaveFacilityAndLocationSettingsMutation } from '../../services/useSaveFacilityAndLocationSettingsMutation';
import style from './style.module.scss';

interface IProps {
  row: IFacilityLevelActiveWithMultiplicationFactorValue;
}

const CostSavingCalculationInput: React.FC<IProps> = ({ row }) => {
  const { t } = useTranslation();
  const { setActiveModal, setSecondaryModal } = useModalActions();

  const [customerMultiplicationFactor, setCustomerMultiplicationFactor] = useState('');
  const [isStartedChangingMultiplicationFactor, setIsStartedChangingMultiplicationFactor] = useState(false);
  const [customerMultiplicationFactorError, setCustomerMultiplicationFactorError] = useState<
    | {
        message: string;
      }
    | undefined
  >(undefined);

  const { data: profileData } = useUserProfileQuery();
  const { mutate: saveFacilityAndLocationSettingsMutate } = useSaveFacilityAndLocationSettingsMutation();
  const { mutate: deleteFacilityAndLocationSettingsMutate } = useDeleteSettingsMutation();

  const isFacility = row.levelName === 'facility';

  const setSettingsChanges = ({
    recalculate,
    saveData,
    deleteData,
  }: {
    recalculate: number;
    saveData?: ISaveFacilityAndLocationSettingsData;
    deleteData?: IDeleteFacilityAndLocationSettingsData;
  }) => {
    if (deleteData) {
      deleteFacilityAndLocationSettingsMutate(
        {
          setting_id: deleteData.settingId,
          setting_type: deleteData.settingType,
          recalculate_cost_saving: recalculate,
        },
        {
          onSuccess: () => {
            showToast({ variant: 'success', message: t('Setting deleted successfully.') });

            setCustomerMultiplicationFactor('');
          },
        },
      );
    }
    if (saveData) {
      const { itemId } = saveData;
      saveFacilityAndLocationSettingsMutate(
        {
          recalculate_cost_saving: recalculate,
          customer_id: profileData?.customer.customer_id,
          ...(isFacility ? { facility_id: itemId } : { location_id: itemId }),
          multiplication_factor: parseFloat(customerMultiplicationFactor),
        },
        {
          onSuccess: () => {
            showToast({ variant: 'success', message: t('Your changes have been saved.') });

            setIsStartedChangingMultiplicationFactor(false);
          },
        },
      );
    }
  };

  const showRecalculateConfirmModal = ({
    saveData,
    deleteData,
  }: {
    saveData?: ISaveFacilityAndLocationSettingsData;
    deleteData?: IDeleteFacilityAndLocationSettingsData;
  }) => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Recalculate archived action items?'),
        bodyContent: t(
          'After saving a new multiplication factor value, do existing completion reports (open and archive) need to be recalculated?',
        ),
        onConfirm: () => setSettingsChanges({ recalculate: 1, saveData, deleteData }),
        onCancel: () => setSettingsChanges({ recalculate: 0, saveData, deleteData }),
        confirmText: t('Recalculate'),
        cancelText: t("Don't Recalculate", {}, true),
      },
    });
  };

  const handleResetToDefault = (settingId: number) => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Delete setting'),
        bodyContent: t('Are you sure you want to delete this setting?'),
        onConfirm: () =>
          showRecalculateConfirmModal({
            deleteData: {
              settingId: settingId,
              settingType: isFacility ? 'facility' : 'asset',
            },
          }),
      },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/,/g, '');
    const numericValue = parseFloat(input);

    if (/^\d*\.?\d*$/.test(input)) {
      setIsStartedChangingMultiplicationFactor(true);
      setCustomerMultiplicationFactor(input ? input : '0');
    }

    if (isNaN(numericValue) || numericValue <= 0) {
      setCustomerMultiplicationFactorError({
        message: 'You cannot set the multiplication factor to less than or equal to 0.',
      });
    } else {
      setCustomerMultiplicationFactorError(undefined);
    }
  };

  return (
    <div className={style.costSavingCalculationInputWrapper}>
      <div className={style.inputContainer}>
        <Input
          leftElement={<span className={style.startAdornment}>{row.currency.symbol}</span>}
          value={
            formatToCurrencyCeil(customerMultiplicationFactor.toString()) ||
            formatToCurrencyCeil(row.currentMultiplicationFactorValue.toString())
          }
          onChange={handleChange}
          type={customerMultiplicationFactorError ? undefined : 'woHelperText'}
          error={customerMultiplicationFactorError}
        />
      </div>
      <div className={style.rightSideWrapper}>
        {isStartedChangingMultiplicationFactor && !customerMultiplicationFactorError && (
          <div className={style.actionsBlock}>
            <Button
              text={t('Apply')}
              type="secondary"
              onClick={() =>
                showRecalculateConfirmModal({
                  saveData: {
                    itemId: isFacility ? row.facilityId : row.id,
                  },
                })
              }
            />
            <span className={style.separator} />
            <Button
              text={t('Reset')}
              type="secondary"
              onClick={() => {
                setIsStartedChangingMultiplicationFactor(false);
                setCustomerMultiplicationFactor('');
              }}
            />
          </div>
        )}
        {row.settingId && !isStartedChangingMultiplicationFactor && !customerMultiplicationFactorError && (
          <Button
            text={t('Reset To Default Value')}
            type="secondary"
            onClick={() => row.settingId && handleResetToDefault(row.settingId)}
          />
        )}
      </div>
    </div>
  );
};

export default CostSavingCalculationInput;
